'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useOrganization } from '@/contexts/OrganizationContext';
import { buildOrgPath } from '@/lib/org-url';
import type { Signal, SignalCategory, SignalFeedResponse, SignalFilterOptions, UnreadCountResponse } from '@/types/signals';

// ==================== Context Type ====================

interface SignalFeedContextValue {
  /** Filtered signal list used by the Signals Log tab. */
  signals: Signal[];
  /** Always-fresh, unfiltered signals for the home-page panel (top 10). */
  panelSignals: Signal[];
  totalCount: number;
  unreadCount: number;
  byPriority: UnreadCountResponse['byPriority'];
  featureEnabled: boolean;
  loading: boolean;
  loadingMore: boolean;
  /** Set when the last fetchSignals call failed (transient backend error). */
  fetchError: string | null;
  nextCursor: string | null;
  /** Mark a single signal as read (optimistic). */
  markRead: (signalId: string) => Promise<void>;
  /** Mark all signals as read (optimistic). */
  markAllRead: () => Promise<void>;
  /** Refresh the signal list from scratch (used by Signals Log with filters). */
  refreshSignals: (filters?: SignalFilterOptions) => Promise<void>;
  /** Load the next page of signals (cursor pagination). */
  loadMore: () => Promise<void>;
}

const defaultByPriority = { urgent: 0, high: 0, medium: 0, low: 0 };

const SignalFeedContext = createContext<SignalFeedContextValue>({
  signals: [],
  panelSignals: [],
  totalCount: 0,
  unreadCount: 0,
  byPriority: defaultByPriority,
  featureEnabled: false,
  loading: true,
  loadingMore: false,
  fetchError: null,
  nextCursor: null,
  markRead: async () => {},
  markAllRead: async () => {},
  refreshSignals: async () => {},
  loadMore: async () => {},
});

// ==================== Helpers ====================

const API_BASE = '/api/ai-services';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const status = body?.featureEnabled === false ? 503 : res.status;
    const err = Object.assign(new Error(body?.error || body?.detail || `HTTP ${res.status}`), { status, _debug: body?._debug });
    throw err;
  }
  const body = await res.json();
  // Backend returns 200 with featureEnabled:false when signals aren't available
  if (body?.featureEnabled === false) {
    const err = Object.assign(new Error('Feature disabled'), { status: 503, _debug: body?._debug });
    throw err;
  }
  return body;
}

// ==================== Provider ====================

const URGENT_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
// Lower-frequency refresh of the general (non-urgent) filtered list, so
// daily/hourly-cadence categories (e.g. activity, product changes) show up
// without the user needing to change filters or manually refresh.
const LIST_POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

export function SignalFeedProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status: authStatus } = useSession();
  const router = useRouter();
  const { orgId: urlOrgId } = useOrganization();
  const isAuthenticated = authStatus === 'authenticated' && !!session?.user;
  // This provider is mounted app-wide, including org-agnostic pages with no
  // [orgId] in the URL — prefer the URL's org id, fall back to the session's
  // active org (still correct, just not reflected in the current URL).
  const navigateToAccount = (path: string) => {
    const orgId = urlOrgId ?? session?.user?.activeOrganizationId;
    router.push(orgId ? buildOrgPath(orgId, path) : `/${path}`);
  };

  // State
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  /** Independent state for the home-page panel – never overwritten by Signals Log filters. */
  const [panelSignals, setPanelSignals] = useState<Signal[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [byPriority, setByPriority] = useState<UnreadCountResponse['byPriority']>(defaultByPriority);

  // Refs for polling
  const lastUrgentPollRef = useRef<string>(new Date().toISOString());
  const urgentTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const listTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeFiltersRef = useRef<SignalFilterOptions>({});

  // ---- Feature-flag probe ----
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        console.log('[SignalFeed] Probing unread-count endpoint...');
        const res = await apiFetch<UnreadCountResponse>('/api/v1/signals/unread-count');
        if (cancelled) return;
        console.log('[SignalFeed] Probe SUCCESS — unreadCount:', res.unreadCount, 'byPriority:', res.byPriority);
        setFeatureEnabled(true);
        setUnreadCount(res.unreadCount ?? 0);
        setByPriority(res.byPriority ?? defaultByPriority);
      } catch (err: any) {
        if (cancelled) return;
        if (err.status === 503) {
          // Genuine "feature disabled for this org" — hide the feature, as before.
          console.warn('[SignalFeed] Probe returned 503 (feature disabled). _debug:', (err as any)._debug);
          setFeatureEnabled(false);
        } else {
          // Transient error (network, 5xx, etc.) — don't conflate this with
          // the org lacking the feature. Assume enabled so tabs/panels render
          // and their own fetches surface the existing "Failed to load
          // signals" retry UI instead of the feature silently vanishing.
          console.error('[SignalFeed] Probe FAILED with transient error (assuming enabled):', err.message, '| status:', err.status, '| _debug:', (err as any)._debug);
          setFeatureEnabled(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ---- Fetch signals ----
  // `silent`: used by the background list poll — updates data without toggling
  // loading/error state, so it doesn't flash the skeleton loader or error card
  // over an already-rendered list.
  const fetchSignals = useCallback(async (filters: SignalFilterOptions = {}, append = false, silent = false) => {
    if (!featureEnabled) return;
    if (!silent) {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      setFetchError(null);
    }

    try {
      const url = buildSignalsUrl(filters);
      console.log('[SignalFeed] fetchSignals →', url);
      const res = await apiFetch<SignalFeedResponse>(url);
      console.log('[SignalFeed] fetchSignals response: totalCount=', res.totalCount, 'signals=', res.signals?.length, '_debug=', (res as any)._debug);
      if (append) {
        setSignals(prev => [...prev, ...normalizeSignals(res.signals)]);
      } else {
        setSignals(normalizeSignals(res.signals));
      }
      setNextCursor(res.nextCursor ?? null);
      setTotalCount(res.totalCount ?? 0);
    } catch (err: any) {
      const msg = err.message || 'Failed to load signals';
      console.error('[SignalFeed] fetchSignals FAILED:', msg, '| status:', err.status, '| _debug:', (err as any)._debug);
      if (!silent) {
        setFetchError(msg);
      }
      // Don't clear existing signals on error so previous results remain visible
    } finally {
      if (!silent) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [featureEnabled]);

  // ---- Fetch panel signals (always unfiltered, for home-page panel) ----
  const fetchPanelSignals = useCallback(async () => {
    if (!featureEnabled) return;
    try {
      const res = await apiFetch<SignalFeedResponse>(buildSignalsUrl({ limit: 10 }));
      setPanelSignals(normalizeSignals(res.signals));
    } catch (err: any) {
      console.warn('[SignalFeed] fetchPanelSignals failed:', err.message);
    }
  }, [featureEnabled]);

  // Initial signal load once feature flag is confirmed
  useEffect(() => {
    if (featureEnabled) {
      fetchSignals({ limit: 50 });
      fetchPanelSignals();
    }
  }, [featureEnabled, fetchSignals, fetchPanelSignals]);

  // ---- Public actions ----

  const refreshSignals = useCallback(async (filters?: SignalFilterOptions) => {
    activeFiltersRef.current = filters ?? {};
    await fetchSignals({ limit: 50, ...filters });
    // Also refresh unread count
    try {
      const res = await apiFetch<UnreadCountResponse>('/api/v1/signals/unread-count');
      setUnreadCount(res.unreadCount ?? 0);
      setByPriority(res.byPriority ?? defaultByPriority);
    } catch { /* ignore */ }
  }, [fetchSignals]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    await fetchSignals({ ...activeFiltersRef.current, limit: 50, cursor: nextCursor }, true);
  }, [nextCursor, loadingMore, fetchSignals]);

  const markRead = useCallback(async (signalId: string) => {
    const now = new Date().toISOString();
    // Optimistic update for both lists
    setSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: true, readAt: now } : s));
    setPanelSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: true, readAt: now } : s));
    setUnreadCount(c => Math.max(0, c - 1));

    try {
      await apiFetch(`/api/v1/signals/${signalId}/read`, { method: 'POST' });
    } catch (err: any) {
      console.error('[SignalFeed] markRead error:', err.message);
      // Revert optimistic update
      setSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: false, readAt: undefined } : s));
      setPanelSignals(prev => prev.map(s => s.id === signalId ? { ...s, read: false, readAt: undefined } : s));
      setUnreadCount(c => c + 1);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const previousSignals = signals;
    const previousPanelSignals = panelSignals;
    const previousCount = unreadCount;
    const now = new Date().toISOString();

    // Optimistic update for both lists
    setSignals(prev => prev.map(s => ({ ...s, read: true, readAt: now })));
    setPanelSignals(prev => prev.map(s => ({ ...s, read: true, readAt: now })));
    setUnreadCount(0);
    setByPriority(defaultByPriority);

    try {
      await apiFetch('/api/v1/signals/read-all', { method: 'POST' });
    } catch (err: any) {
      console.error('[SignalFeed] markAllRead error:', err.message);
      setSignals(previousSignals);
      setPanelSignals(previousPanelSignals);
      setUnreadCount(previousCount);
    }
  }, [signals, panelSignals, unreadCount]);

  // ---- Urgent signal polling ----
  const pollUrgent = useCallback(async () => {
    if (!featureEnabled) return;
    try {
      const since = lastUrgentPollRef.current;
      lastUrgentPollRef.current = new Date().toISOString();

      const res = await apiFetch<SignalFeedResponse>(
        buildSignalsUrl({ priority: 'urgent', since, read: false, limit: 10 }),
      );

      for (const signal of normalizeSignals(res.signals)) {
        toast(signal.title, {
          description: signal.accountName,
          action: {
            label: 'View',
            onClick: () => navigateToAccount(`customer/${signal.accountId}`),
          },
          duration: 8000,
        });
      }

      // Refresh unread count + panel signals after urgent poll
      if (res.signals.length > 0) {
        const countRes = await apiFetch<UnreadCountResponse>('/api/v1/signals/unread-count');
        setUnreadCount(countRes.unreadCount ?? 0);
        setByPriority(countRes.byPriority ?? defaultByPriority);
        // Refresh panel signals to pick up newly arrived signals
        const panelRes = await apiFetch<SignalFeedResponse>(buildSignalsUrl({ limit: 10 }));
        setPanelSignals(normalizeSignals(panelRes.signals));
      }
    } catch (err: any) {
      console.warn('[SignalFeed] urgentPoll error:', err.message);
    }
  }, [featureEnabled, router]);

  // Start/stop urgent polling + Page Visibility
  useEffect(() => {
    if (!featureEnabled || !isAuthenticated) return;

    const startPolling = () => {
      if (urgentTimerRef.current) return;
      urgentTimerRef.current = setInterval(pollUrgent, URGENT_POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (urgentTimerRef.current) {
        clearInterval(urgentTimerRef.current);
        urgentTimerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Immediate poll on tab focus, then resume interval
        pollUrgent();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [featureEnabled, isAuthenticated, pollUrgent]);

  // ---- Silent periodic refresh of the general filtered list ----
  // Unlike pollUrgent (priority: 'urgent' only), this re-fetches whatever
  // filters are currently active so daily/hourly-cadence signal categories
  // become visible without a manual refresh or filter change.
  const pollList = useCallback(async () => {
    if (!featureEnabled) return;
    await fetchSignals({ limit: 50, ...activeFiltersRef.current }, false, true);
  }, [featureEnabled, fetchSignals]);

  useEffect(() => {
    if (!featureEnabled || !isAuthenticated) return;

    const startPolling = () => {
      if (listTimerRef.current) return;
      listTimerRef.current = setInterval(pollList, LIST_POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (listTimerRef.current) {
        clearInterval(listTimerRef.current);
        listTimerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [featureEnabled, isAuthenticated, pollList]);

  // ---- Real-time delivery (SSE bridge, Phase 2) ----
  // Polling above stays as the fallback for browsers/proxies that drop
  // long-lived SSE connections — this stream is additive, not a replacement.
  useEffect(() => {
    if (!featureEnabled || !isAuthenticated) return;
    if (process.env.NEXT_PUBLIC_NOTIFICATIONS_SSE_ENABLED === 'false') return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    const currentUserId = session?.user?.id;
    let closed = false;
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 2000;
    const MAX_RETRY_DELAY_MS = 30000;

    const connect = () => {
      if (closed) return;
      es = new EventSource('/api/notifications-proxy/stream');

      es.onopen = () => {
        retryDelay = 2000;
      };

      es.addEventListener('signal', (evt) => {
        try {
          const raw = JSON.parse((evt as MessageEvent).data);
          // Org-wide signals (target_user_id null) are visible to everyone;
          // user-scoped system notifications only to their recipient —
          // mirrors the visibility rule urgent_signal_service.list_signals applies.
          if (raw.target_user_id && raw.target_user_id !== currentUserId) return;

          const signal = normalizeSignals([raw])[0];
          if (!signal?.id) return;

          setSignals(prev => (prev.some(s => s.id === signal.id) ? prev : [signal, ...prev]));
          setPanelSignals(prev =>
            prev.some(s => s.id === signal.id) ? prev : [signal, ...prev].slice(0, 10)
          );
          setUnreadCount(c => c + 1);
          setByPriority(prev => ({
            ...prev,
            [signal.priority]: (prev[signal.priority] ?? 0) + 1,
          }));

          toast(signal.title, {
            description: signal.accountName || undefined,
            action: signal.accountId
              ? { label: 'View', onClick: () => navigateToAccount(`customer/${signal.accountId}`) }
              : undefined,
            duration: 8000,
          });
        } catch (err) {
          console.warn('[SignalFeed] SSE signal parse error:', err);
        }
      });

      es.addEventListener('signal_suggestions', (evt) => {
        // Signal Engine: patch suggestionCount onto the matching signal by
        // signal_id. Delivered as a distinct event type (not folded into
        // 'signal' above) since suggestions are computed slightly after the
        // signal write in a later pipeline step — a separate event avoids
        // guessing whether an absent count means "zero" or "not yet computed".
        try {
          const raw = JSON.parse((evt as MessageEvent).data);
          const signalId = raw.signal_id;
          const count = raw.suggestion_count ?? 0;
          if (!signalId) return;

          const patch = (list: Signal[]) =>
            list.map(s => (s.id === signalId ? { ...s, suggestionCount: count } : s));
          setSignals(patch);
          setPanelSignals(patch);
        } catch (err) {
          console.warn('[SignalFeed] SSE signal_suggestions parse error:', err);
        }
      });

      es.addEventListener('discovery_event', (evt) => {
        // Discovery Workspace UI (not the notification bell) consumes this.
        // Broadcast as a DOM event so those components can subscribe without
        // this context needing to know about discovery-specific payloads.
        window.dispatchEvent(
          new CustomEvent('discovery-event', { detail: (evt as MessageEvent).data })
        );
      });

      es.onerror = () => {
        // A CLOSED readyState means the backend ended the connection (e.g. a
        // 503 while the feature flag is off) — native EventSource does not
        // auto-retry from CLOSED, so back off and reopen ourselves. A
        // CONNECTING readyState means EventSource is already retrying on its
        // own; no action needed.
        if (es?.readyState === EventSource.CLOSED && !closed) {
          es.close();
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS);
        }
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
    };
  }, [featureEnabled, isAuthenticated, session?.user?.id, router]);

  return (
    <SignalFeedContext.Provider
      value={{
        signals,
        panelSignals,
        totalCount,
        unreadCount,
        byPriority,
        featureEnabled,
        loading,
        loadingMore,
        fetchError,
        nextCursor,
        markRead,
        markAllRead,
        refreshSignals,
        loadMore,
      }}
    >
      {children}
    </SignalFeedContext.Provider>
  );
}

// ==================== Hook ====================

export function useSignalFeed() {
  return useContext(SignalFeedContext);
}

// ==================== Utilities ====================

function buildSignalsUrl(filters: SignalFilterOptions): string {
  const params = new URLSearchParams();
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.accountId) params.append('account_id', filters.accountId);
  if (filters.connector) params.append('connector', filters.connector);
  if (filters.category && filters.category.length > 0) params.append('category', filters.category.join(','));
  if (filters.since) params.append('since', filters.since);
  if (filters.read !== undefined) params.append('read', String(filters.read));
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters.cursor) params.append('cursor', filters.cursor);
  if (filters.viewAll) params.append('viewAll', 'true');
  const qs = params.toString();
  return `/api/v1/signals${qs ? `?${qs}` : ''}`;
}

/**
 * Derive a SignalCategory from the backend signal_type string.
 * Backend uses formats like "gong:sentiment", "expansion:opportunity", "churn:risk".
 * The second segment (or the whole string) is matched to a known category.
 */
function categoryFromSignalType(signalType?: string): SignalCategory {
  if (!signalType) return 'other';
  const lower = signalType.toLowerCase();
  if (lower.includes('expansion')) return 'expansion';
  if (lower.includes('churn')) return 'churn_risk';
  if (lower.includes('escalat')) return 'escalation';
  if (lower.includes('sentiment')) return 'sentiment';
  if (lower.includes('contact')) return 'contact_changes';
  if (lower.includes('health')) return 'health_scores';
  if (lower.includes('engagement')) return 'engagement_decline';
  if (lower.includes('renewal')) return 'renewal';
  if (lower.includes('support')) return 'tickets';
  if (lower.includes('usage') || lower.includes('product')) return 'usage';
  return 'other';
}

/** Normalize snake_case keys from backend to camelCase Signal objects. */
function normalizeSignals(raw: any[]): Signal[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((s: any) => ({
    // Backend may use signal_id, id, or _id
    id: s.id ?? s._id ?? s.signal_id ?? '',
    title: s.title ?? '',
    description: s.description ?? '',
    priority: s.priority ?? 'low',
    // Backend may use category directly or encode it in signal_type
    category: s.category ?? categoryFromSignalType(s.signal_type),
    // Backend may use connector, connector_type, or connectorType
    connectorType: s.connector_type ?? s.connectorType ?? s.connector ?? 'system',
    accountId: s.account_id ?? s.accountId ?? '',
    accountName: s.account_name ?? s.accountName ?? '',
    read: s.read ?? false,
    readAt: s.read_at ?? s.readAt,
    createdAt: s.created_at ?? s.createdAt ?? s.created_time ?? new Date().toISOString(),
    updatedAt: s.updated_at ?? s.updatedAt,
    evidence: s.evidence,
    metadata: s.metadata ?? s.details,
  }));
}
